import {
    CalendarSyncDirection,
    CalendarSyncStatus,
    Calendar as OwnitCalendar,
    EntityId,
    Event as OwnitEvent,
    ExternalCalendarDeletionPolicy,
    ExternalCalendarWritePermissionStatus,
    ExternalCalendarProviderType,
    ExternalCalendarSource,
    ExternalEventMapping,
    ISODateString,
    ISODateTimeString,
    RepeatRule,
    TimeString,
    User
} from './models';
import { OWNIT_NEUTRAL_COLORS, addDays, createDateKey } from './sample-data';

export type ExternalCalendarProviderStatus = 'mock' | 'ready' | 'unavailable';
export type ExternalOAuthProviderId = Extract<ExternalCalendarProviderType, 'google' | 'naver' | 'microsoft'>;
export type ExternalOAuthConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'cancelled' | 'error';
export type ExternalCalendarIncrementalSyncMode = 'syncToken' | 'deltaLink' | 'manual' | 'not_available';
export type ExternalCalendarRecurrenceMode = 'rrule' | 'microsoft-patterned-recurrence' | 'ical';
export type ExternalCalendarAllDayMode = 'date' | 'isAllDay-midnight' | 'ical';
export type ExternalCalendarTimezoneMode = 'iana' | 'windows-or-iana' | 'ical-vtimezone';
export type ExternalCalendarConflictPolicy = 'etag_or_updated' | 'changekey' | 'server_required';
export type ExternalCalendarAdapterKind = 'oauth_api' | 'caldav' | 'ics_url' | 'mock';

export const EXTERNAL_CALENDAR_PROVIDER_IDS: ExternalCalendarProviderType[] = ['google', 'microsoft', 'apple', 'naver', 'ics_url'];

export const EXTERNAL_EVENT_FIELD_MAPPING = [
    { field: 'externalId', ownitTarget: 'Event.externalId / ExternalEventMapping.externalEventId', externalSource: 'Provider event id or iCalendar UID' },
    { field: 'provider', ownitTarget: 'Calendar.provider / Event.provider / ExternalEventMapping.provider', externalSource: 'google, microsoft, apple, naver, ics_url adapter id' },
    { field: 'calendarName', ownitTarget: 'Calendar.name / Event.externalCalendarName / ExternalEventMapping.calendarName', externalSource: 'Provider calendar display name' },
    { field: 'title', ownitTarget: 'Event.title', externalSource: 'summary / subject / iCalendar SUMMARY' },
    { field: 'startDate/startDateTime', ownitTarget: 'Event.startDate + Event.startTime', externalSource: 'All-day date or timed dateTime' },
    { field: 'endDate/endDateTime', ownitTarget: 'Event.endDate + Event.endTime', externalSource: 'All-day date or timed dateTime' },
    { field: 'allDay', ownitTarget: 'Event.allDay', externalSource: 'date-only start/end, isAllDay, or iCalendar VALUE=DATE' },
    { field: 'timezone', ownitTarget: 'Event.externalTimezone / ExternalEventMapping.timezone', externalSource: 'Provider timeZone, VTIMEZONE, or local fallback' },
    { field: 'color', ownitTarget: 'Calendar.color / Event.color', externalSource: 'Calendar color or adapter fallback color' },
    { field: 'readOnly', ownitTarget: 'Calendar.readOnly / Event.readOnly', externalSource: 'Always true for import MVP' }
] as const;

export interface ExternalCalendarProviderContext {
    userId: EntityId;
    rangeStart: ISODateString;
    rangeEnd: ISODateString;
    syncedAt: ISODateTimeString;
}

export interface ExternalCalendarIntegrationReference {
    source: ExternalCalendarProviderType;
    provider: ExternalCalendarProviderType;
    label: string;
    docsUrl: string;
    readScope: string;
    eventListEndpoint: string;
    note: string;
}

export interface ExternalCalendarProviderCapabilities {
    readonlyDefault: true;
    import: boolean;
    export: boolean;
    bidirectional: boolean;
    recurrence: ExternalCalendarRecurrenceMode;
    allDay: ExternalCalendarAllDayMode;
    timezone: ExternalCalendarTimezoneMode;
    incrementalSync: ExternalCalendarIncrementalSyncMode;
    deletionPolicy: ExternalCalendarDeletionPolicy;
    conflictPolicy: ExternalCalendarConflictPolicy;
}

export interface ExternalOAuthProviderConfig extends ExternalCalendarIntegrationReference {
    id: ExternalOAuthProviderId;
    authEndpoint: string;
    tokenEndpoint: string;
    tokenExchange: 'server-required';
    redirectPath: string;
    scopes: string[];
    writeScopes: string[];
    clientIdEnvKeys: string[];
    authorizationParams?: Record<string, string>;
    capabilities: ExternalCalendarProviderCapabilities;
    tokenStoragePolicy: string;
    callbackHandling: string;
}

export interface ExternalOAuthConnection {
    provider: ExternalOAuthProviderId;
    status: ExternalOAuthConnectionStatus;
    readOnly: boolean;
    scope: string;
    writeEnabled: boolean;
    writeScope: string;
    writeScopeRequestedAt: ISODateTimeString | null;
    connectedAt: ISODateTimeString | null;
    lastSyncedAt: ISODateTimeString | null;
    updatedAt: ISODateTimeString;
    lastMessage: string;
}

export interface ExternalOAuthStartRequest {
    provider: ExternalOAuthProviderId;
    clientId: string;
    redirectUri: string;
    state: string;
    scopes?: string[];
}

export interface ExternalOAuthStartResult {
    provider: ExternalOAuthProviderId;
    url: string;
    state: string;
    redirectUri: string;
    scopes: string[];
    isReady: boolean;
    missingConfig: string[];
    tokenExchange: ExternalOAuthProviderConfig['tokenExchange'];
}

export interface ExternalCalendarProviderSummary extends ExternalCalendarIntegrationReference {
    status: ExternalCalendarProviderStatus;
    calendarCount: number;
    eventCount: number;
    readOnly: boolean;
    capabilities: ExternalCalendarProviderCapabilities;
}

export interface ExternalCalendar {
    externalId: string;
    source: ExternalCalendarSource;
    provider: ExternalCalendarProviderType;
    name: string;
    description: string;
    color: string;
    isVisible: boolean;
    readOnly: true;
    lastSyncedAt: ISODateTimeString;
    order: number;
}

export interface ExternalCalendarEvent {
    externalId: string;
    calendarExternalId: string;
    source: ExternalCalendarSource;
    provider: ExternalCalendarProviderType;
    calendarName: string;
    title: string;
    description: string;
    startDate: ISODateString;
    endDate: ISODateString;
    startTime: TimeString | null;
    endTime: TimeString | null;
    allDay: boolean;
    color?: string;
    location?: string;
    updatedAt?: ISODateTimeString;
    timezone?: string;
    recurringEventId?: string | null;
    repeatRule?: RepeatRule | null;
    deleted?: boolean;
    readOnly: true;
    lastSyncedAt: ISODateTimeString;
}

export interface ExternalCalendarProvider {
    id: ExternalCalendarProviderType;
    label: string;
    status: ExternalCalendarProviderStatus;
    adapterKind: ExternalCalendarAdapterKind;
    readOnly: true;
    reference: ExternalCalendarIntegrationReference;
    capabilities: ExternalCalendarProviderCapabilities;
    listCalendars(context: ExternalCalendarProviderContext): ExternalCalendar[];
    listEvents(calendar: ExternalCalendar, context: ExternalCalendarProviderContext): ExternalCalendarEvent[];
}

export interface ExternalCalendarSnapshot {
    calendars: OwnitCalendar[];
    events: OwnitEvent[];
    mappings: ExternalEventMapping[];
    providers: ExternalCalendarProviderSummary[];
}

interface MockProviderConfig {
    id: ExternalCalendarProviderType;
    label: string;
    adapterKind: ExternalCalendarAdapterKind;
    color: string;
    order: number;
    calendarName: string;
    description: string;
    reference: ExternalCalendarIntegrationReference;
    capabilities: ExternalCalendarProviderCapabilities;
    events(anchorDate: Date): Array<Omit<ExternalCalendarEvent, 'calendarExternalId' | 'source' | 'provider' | 'calendarName' | 'color' | 'readOnly' | 'lastSyncedAt'>>;
}

export const GOOGLE_CALENDAR_READONLY_SCOPES = [
    'https://www.googleapis.com/auth/calendar.calendarlist.readonly',
    'https://www.googleapis.com/auth/calendar.events.readonly'
];

export const GOOGLE_CALENDAR_WRITE_SCOPES = [
    'https://www.googleapis.com/auth/calendar.events'
];

export const MICROSOFT_CALENDAR_READONLY_SCOPES = [
    'offline_access',
    'Calendars.Read'
];

export const MICROSOFT_CALENDAR_WRITE_SCOPES = [
    'Calendars.ReadWrite'
];

const NAVER_CALENDAR_READONLY_SCOPES: string[] = [];
const NAVER_CALENDAR_WRITE_SCOPES: string[] = [];

const EXTERNAL_CALENDAR_TOKEN_STORAGE_POLICY = 'client secret, authorization code, access token, refresh token은 브라우저 저장소에 저장하지 않고 backend/serverless token vault에서만 교환/암호화 저장합니다.';

const GOOGLE_CALENDAR_CAPABILITIES: ExternalCalendarProviderCapabilities = {
    readonlyDefault: true,
    import: true,
    export: true,
    bidirectional: true,
    recurrence: 'rrule',
    allDay: 'date',
    timezone: 'iana',
    incrementalSync: 'syncToken',
    deletionPolicy: 'mark_deleted_only',
    conflictPolicy: 'etag_or_updated'
};

const NAVER_CALENDAR_CAPABILITIES: ExternalCalendarProviderCapabilities = {
    readonlyDefault: true,
    import: false,
    export: true,
    bidirectional: false,
    recurrence: 'ical',
    allDay: 'ical',
    timezone: 'ical-vtimezone',
    incrementalSync: 'not_available',
    deletionPolicy: 'mark_deleted_only',
    conflictPolicy: 'server_required'
};

const MICROSOFT_CALENDAR_CAPABILITIES: ExternalCalendarProviderCapabilities = {
    readonlyDefault: true,
    import: true,
    export: true,
    bidirectional: true,
    recurrence: 'microsoft-patterned-recurrence',
    allDay: 'isAllDay-midnight',
    timezone: 'windows-or-iana',
    incrementalSync: 'deltaLink',
    deletionPolicy: 'mark_deleted_only',
    conflictPolicy: 'changekey'
};

const APPLE_CALENDAR_CAPABILITIES: ExternalCalendarProviderCapabilities = {
    readonlyDefault: true,
    import: true,
    export: false,
    bidirectional: false,
    recurrence: 'ical',
    allDay: 'ical',
    timezone: 'ical-vtimezone',
    incrementalSync: 'manual',
    deletionPolicy: 'mark_deleted_only',
    conflictPolicy: 'server_required'
};

const ICS_URL_CALENDAR_CAPABILITIES: ExternalCalendarProviderCapabilities = {
    readonlyDefault: true,
    import: true,
    export: false,
    bidirectional: false,
    recurrence: 'ical',
    allDay: 'ical',
    timezone: 'ical-vtimezone',
    incrementalSync: 'manual',
    deletionPolicy: 'mark_deleted_only',
    conflictPolicy: 'server_required'
};

const INTEGRATION_REFERENCES: Record<ExternalCalendarProviderType, ExternalCalendarIntegrationReference> = {
    google: {
        source: 'google',
        provider: 'google',
        label: 'Google Calendar',
        docsUrl: 'https://developers.google.com/workspace/calendar/api/v3/reference/events/list',
        readScope: GOOGLE_CALENDAR_READONLY_SCOPES.join(' '),
        eventListEndpoint: 'GET /calendar/v3/users/me/calendarList, GET /calendar/v3/calendars/{calendarId}/events',
        note: 'CalendarList와 Events list를 읽기 전용 scope만 요청해 연결합니다.'
    },
    microsoft: {
        source: 'microsoft',
        provider: 'microsoft',
        label: 'Microsoft Outlook',
        docsUrl: 'https://learn.microsoft.com/en-us/graph/api/calendar-list-events?view=graph-rest-1.0',
        readScope: MICROSOFT_CALENDAR_READONLY_SCOPES.join(' '),
        eventListEndpoint: 'GET /me/calendars/{id}/events 또는 /calendarView',
        note: 'Microsoft Graph CalendarView/Events와 deltaLink 기반 증분 동기화로 확장합니다.'
    },
    apple: {
        source: 'apple',
        provider: 'apple',
        label: 'Apple Calendar',
        docsUrl: '',
        readScope: 'Apple Calendar는 브라우저 OAuth가 아니라 서버 CalDAV/ICS URL adapter 검증 후 연결합니다.',
        eventListEndpoint: 'CalDAV REPORT 또는 사용자가 제공한 iCloud 공개 ICS URL fetch',
        note: '현재 MVP에서는 Apple Calendar를 서버/ICS adapter 경계로만 정의하고 자동 쓰기는 지원하지 않습니다.'
    },
    naver: {
        source: 'naver',
        provider: 'naver',
        label: 'Naver Calendar',
        docsUrl: 'https://developers.naver.com/docs/login/calendar-api/calendar-api.md',
        readScope: 'Naver Login OAuth 2.0, calendar read/list 공개 문서 미확인',
        eventListEndpoint: '공식 공개 문서는 createSchedule 중심입니다. 읽기 API는 서버 검증 전까지 mock/unavailable로 둡니다.',
        note: '공식 문서상 캘린더 일정 추가 API는 iCalendar createSchedule 방식입니다. 읽기와 양방향 동기화는 서버 검증 전까지 켜지지 않습니다.'
    },
    ics_url: {
        source: 'ics_url',
        provider: 'ics_url',
        label: 'ICS URL',
        docsUrl: 'https://datatracker.ietf.org/doc/html/rfc5545',
        readScope: '공개 또는 사용자가 입력한 ICS URL을 서버 proxy에서 읽기 전용으로 가져옵니다.',
        eventListEndpoint: 'GET {icsUrl} -> iCalendar VEVENT parse',
        note: 'OAuth 없이 시작할 수 있는 읽기 전용 adapter입니다. URL 검증과 SSRF 방지는 서버에서 처리합니다.'
    }
};

export const EXTERNAL_OAUTH_PROVIDER_CONFIGS: Record<ExternalOAuthProviderId, ExternalOAuthProviderConfig> = {
    google: {
        ...INTEGRATION_REFERENCES.google,
        id: 'google',
        authEndpoint: 'https://accounts.google.com/o/oauth2/v2/auth',
        tokenEndpoint: 'https://oauth2.googleapis.com/token',
        tokenExchange: 'server-required',
        redirectPath: '/dashboard/settings',
        scopes: GOOGLE_CALENDAR_READONLY_SCOPES,
        writeScopes: GOOGLE_CALENDAR_WRITE_SCOPES,
        clientIdEnvKeys: [
            'OWNIT_GOOGLE_CALENDAR_CLIENT_ID',
            'NG_APP_OWNIT_GOOGLE_CALENDAR_CLIENT_ID',
            'VITE_OWNIT_GOOGLE_CALENDAR_CLIENT_ID'
        ],
        authorizationParams: {
            access_type: 'offline',
            include_granted_scopes: 'true',
            prompt: 'consent'
        },
        capabilities: GOOGLE_CALENDAR_CAPABILITIES,
        tokenStoragePolicy: EXTERNAL_CALENDAR_TOKEN_STORAGE_POLICY,
        callbackHandling: '/dashboard/settings?external_oauth=google 에서 code/state/error를 확인합니다. 현재 클라이언트 MVP는 code를 저장하지 않고 즉시 URL에서 제거하며, 실제 token exchange는 서버 구현 전까지 실행하지 않습니다.'
    },
    naver: {
        ...INTEGRATION_REFERENCES.naver,
        id: 'naver',
        authEndpoint: 'https://nid.naver.com/oauth2.0/authorize',
        tokenEndpoint: 'https://nid.naver.com/oauth2.0/token',
        tokenExchange: 'server-required',
        redirectPath: '/dashboard/settings',
        scopes: NAVER_CALENDAR_READONLY_SCOPES,
        writeScopes: NAVER_CALENDAR_WRITE_SCOPES,
        clientIdEnvKeys: [
            'OWNIT_NAVER_CALENDAR_CLIENT_ID',
            'NG_APP_OWNIT_NAVER_CALENDAR_CLIENT_ID',
            'VITE_OWNIT_NAVER_CALENDAR_CLIENT_ID'
        ],
        capabilities: NAVER_CALENDAR_CAPABILITIES,
        tokenStoragePolicy: EXTERNAL_CALENDAR_TOKEN_STORAGE_POLICY,
        callbackHandling: '/dashboard/settings?external_oauth=naver 에서 code/state/error를 확인합니다. client secret과 token exchange는 서버에서만 처리합니다.'
    },
    microsoft: {
        ...INTEGRATION_REFERENCES.microsoft,
        id: 'microsoft',
        authEndpoint: 'https://login.microsoftonline.com/common/oauth2/v2.0/authorize',
        tokenEndpoint: 'https://login.microsoftonline.com/common/oauth2/v2.0/token',
        tokenExchange: 'server-required',
        redirectPath: '/dashboard/settings',
        scopes: MICROSOFT_CALENDAR_READONLY_SCOPES,
        writeScopes: MICROSOFT_CALENDAR_WRITE_SCOPES,
        clientIdEnvKeys: [
            'OWNIT_MICROSOFT_CALENDAR_CLIENT_ID',
            'NG_APP_OWNIT_MICROSOFT_CALENDAR_CLIENT_ID',
            'VITE_OWNIT_MICROSOFT_CALENDAR_CLIENT_ID'
        ],
        authorizationParams: {
            response_mode: 'query',
            prompt: 'select_account'
        },
        capabilities: MICROSOFT_CALENDAR_CAPABILITIES,
        tokenStoragePolicy: EXTERNAL_CALENDAR_TOKEN_STORAGE_POLICY,
        callbackHandling: '/dashboard/settings?external_oauth=microsoft 에서 code/state/error를 확인합니다. refresh token은 서버 token vault에서만 저장합니다.'
    }
};

const MOCK_PROVIDER_CONFIGS: MockProviderConfig[] = [
    {
        id: 'google',
        label: 'Google Calendar',
        adapterKind: 'oauth_api',
        color: '#7B7067',
        order: 610,
        calendarName: 'Google 개인 일정',
        description: 'Google Calendar mock provider에서 읽어 온 일정입니다.',
        reference: INTEGRATION_REFERENCES.google,
        capabilities: GOOGLE_CALENDAR_CAPABILITIES,
        events: (anchorDate) => [
            {
                externalId: 'gcal-focus-block',
                title: 'Google 집중 블록',
                description: '외부 캘린더에서 가져온 읽기 전용 개인 일정입니다.',
                startDate: createDateKey(addDays(anchorDate, 1)),
                endDate: createDateKey(addDays(anchorDate, 1)),
                startTime: '14:00',
                endTime: '15:30',
                allDay: false
            }
        ]
    },
    {
        id: 'microsoft',
        label: 'Microsoft Outlook',
        adapterKind: 'oauth_api',
        color: '#786F82',
        order: 620,
        calendarName: 'Outlook 업무 일정',
        description: 'Microsoft Graph mock provider에서 읽어 온 일정입니다.',
        reference: INTEGRATION_REFERENCES.microsoft,
        capabilities: MICROSOFT_CALENDAR_CAPABILITIES,
        events: (anchorDate) => [
            {
                externalId: 'ms-team-sync',
                title: 'Outlook 팀 싱크',
                description: 'Microsoft Graph Calendar 연동을 대비한 mock 일정입니다.',
                startDate: createDateKey(addDays(anchorDate, -1)),
                endDate: createDateKey(addDays(anchorDate, 1)),
                startTime: null,
                endTime: null,
                allDay: true
            }
        ]
    },
    {
        id: 'apple',
        label: 'Apple Calendar',
        adapterKind: 'caldav',
        color: '#6E7480',
        order: 630,
        calendarName: 'Apple 캘린더',
        description: 'Apple Calendar CalDAV/ICS adapter를 대비한 읽기 전용 mock 캘린더입니다.',
        reference: INTEGRATION_REFERENCES.apple,
        capabilities: APPLE_CALENDAR_CAPABILITIES,
        events: (anchorDate) => [
            {
                externalId: 'apple-personal-subscription',
                title: 'Apple 구독 일정',
                description: 'Apple Calendar 읽기 전용 가져오기를 대비한 mock 일정입니다.',
                startDate: createDateKey(addDays(anchorDate, 5)),
                endDate: createDateKey(addDays(anchorDate, 5)),
                startTime: '10:00',
                endTime: '11:00',
                allDay: false,
                timezone: 'Asia/Seoul'
            }
        ]
    },
    {
        id: 'naver',
        label: 'Naver Calendar',
        adapterKind: 'oauth_api',
        color: '#70766A',
        order: 640,
        calendarName: 'Naver 일정함',
        description: 'Naver Calendar mock provider에서 읽어 온 일정입니다.',
        reference: INTEGRATION_REFERENCES.naver,
        capabilities: NAVER_CALENDAR_CAPABILITIES,
        events: (anchorDate) => [
            {
                externalId: 'naver-family-day',
                title: 'Naver 가족 일정',
                description: 'Naver 연동을 대비한 mock 읽기 전용 일정입니다.',
                startDate: createDateKey(addDays(anchorDate, 3)),
                endDate: createDateKey(addDays(anchorDate, 3)),
                startTime: null,
                endTime: null,
                allDay: true
            }
        ]
    },
    {
        id: 'ics_url',
        label: 'ICS URL',
        adapterKind: 'ics_url',
        color: '#7A7569',
        order: 650,
        calendarName: 'ICS 구독 캘린더',
        description: 'ICS URL adapter에서 읽어 온 일정입니다.',
        reference: INTEGRATION_REFERENCES.ics_url,
        capabilities: ICS_URL_CALENDAR_CAPABILITIES,
        events: (anchorDate) => [
            {
                externalId: 'ics-public-deadline',
                title: 'ICS 마감 일정',
                description: 'ICS URL 읽기 전용 가져오기 adapter를 대비한 mock 일정입니다.',
                startDate: createDateKey(addDays(anchorDate, 8)),
                endDate: createDateKey(addDays(anchorDate, 8)),
                startTime: null,
                endTime: null,
                allDay: true
            }
        ]
    }
];

export function createMockExternalCalendarProviders(): ExternalCalendarProvider[] {
    return MOCK_PROVIDER_CONFIGS.map((config) => ({
        id: config.id,
        label: config.label,
        status: 'mock',
        adapterKind: config.adapterKind,
        readOnly: true,
        reference: config.reference,
        capabilities: config.capabilities,
        listCalendars: (context) => [createMockExternalCalendar(config, context)],
        listEvents: (calendar, context) => {
            const anchorDate = new Date(`${context.rangeStart}T00:00:00`);
            return config.events(anchorDate).map((event) => ({
                ...event,
                calendarExternalId: calendar.externalId,
                source: config.id,
                provider: config.id,
                calendarName: calendar.name,
                color: calendar.color,
                readOnly: true,
                lastSyncedAt: context.syncedAt
            }));
        }
    }));
}

export function collectExternalCalendarSnapshot(user: User, providers: ExternalCalendarProvider[], rangeStart: ISODateString, rangeEnd: ISODateString, syncedAt = new Date().toISOString()): ExternalCalendarSnapshot {
    const context: ExternalCalendarProviderContext = {
        userId: user.id,
        rangeStart,
        rangeEnd,
        syncedAt
    };
    const calendars: OwnitCalendar[] = [];
    const events: OwnitEvent[] = [];
    const mappings: ExternalEventMapping[] = [];
    const summaries: ExternalCalendarProviderSummary[] = [];

    for (const provider of providers) {
        const providerCalendars = provider.listCalendars(context);
        let providerEventCount = 0;

        for (const externalCalendar of providerCalendars) {
            const ownitCalendar = mapExternalCalendarToOwnitCalendar(externalCalendar, user, provider, syncedAt);
            const providerEvents = provider.listEvents(externalCalendar, context);
            calendars.push(ownitCalendar);
            for (const event of providerEvents) {
                const ownitEvent = mapExternalEventToOwnitEvent(event, ownitCalendar, user, provider, syncedAt);
                events.push(ownitEvent);
                mappings.push(mapExternalEventToOwnitMapping(event, ownitEvent, ownitCalendar, user, provider, syncedAt));
            }
            providerEventCount += providerEvents.length;
        }

        summaries.push({
            ...provider.reference,
            status: provider.status,
            provider: provider.id,
            calendarCount: providerCalendars.length,
            eventCount: providerEventCount,
            readOnly: provider.readOnly,
            capabilities: provider.capabilities
        });
    }

    return { calendars, events, mappings, providers: summaries };
}

export function getExternalOAuthProviderConfig(provider: ExternalOAuthProviderId): ExternalOAuthProviderConfig | null {
    return EXTERNAL_OAUTH_PROVIDER_CONFIGS[provider] || null;
}

export function isExternalOAuthProviderId(value: any): value is ExternalOAuthProviderId {
    return value === 'google' || value === 'naver' || value === 'microsoft';
}

export function isExternalCalendarProviderId(value: any): value is ExternalCalendarProviderType {
    return value === 'google' || value === 'microsoft' || value === 'apple' || value === 'naver' || value === 'ics_url';
}

export function createExternalOAuthConnection(provider: ExternalOAuthProviderId, patch: Partial<ExternalOAuthConnection> = {}, now = new Date().toISOString()): ExternalOAuthConnection {
    const config = getExternalOAuthProviderConfig(provider);
    return {
        provider,
        status: patch.status || 'disconnected',
        readOnly: patch.readOnly === undefined ? true : patch.readOnly,
        scope: patch.scope || config?.scopes.join(' ') || '',
        writeEnabled: patch.writeEnabled === true,
        writeScope: patch.writeScope || '',
        writeScopeRequestedAt: patch.writeScopeRequestedAt === undefined ? null : patch.writeScopeRequestedAt,
        connectedAt: patch.connectedAt === undefined ? null : patch.connectedAt,
        lastSyncedAt: patch.lastSyncedAt === undefined ? null : patch.lastSyncedAt,
        updatedAt: patch.updatedAt || now,
        lastMessage: patch.lastMessage || '연결 대기 중입니다.'
    };
}

export function buildExternalOAuthStartUrl(request: ExternalOAuthStartRequest): ExternalOAuthStartResult {
    const config = getExternalOAuthProviderConfig(request.provider);
    if (!config) {
        return {
            provider: request.provider,
            url: '',
            state: (request.state || '').trim(),
            redirectUri: (request.redirectUri || '').trim(),
            scopes: request.scopes || [],
            isReady: false,
            missingConfig: ['provider_config'],
            tokenExchange: 'server-required'
        };
    }
    const scopes = request.scopes?.length ? request.scopes : config.scopes;
    const clientId = (request.clientId || '').trim();
    const redirectUri = (request.redirectUri || '').trim();
    const state = (request.state || '').trim();
    const missingConfig: string[] = [];
    if (!clientId) missingConfig.push('client_id');
    if (!redirectUri) missingConfig.push('redirect_uri');
    if (!state) missingConfig.push('state');

    if (missingConfig.length > 0) {
        return {
            provider: request.provider,
            url: '',
            state,
            redirectUri,
            scopes,
            isReady: false,
            missingConfig,
            tokenExchange: config.tokenExchange
        };
    }

    const url = new URL(config.authEndpoint);
    url.searchParams.set('client_id', clientId);
    url.searchParams.set('redirect_uri', redirectUri);
    url.searchParams.set('response_type', 'code');
    url.searchParams.set('state', state);
    if (scopes.length > 0) url.searchParams.set('scope', scopes.join(' '));
    Object.entries(config.authorizationParams || {}).forEach(([key, value]) => {
        if (value) url.searchParams.set(key, value);
    });

    return {
        provider: request.provider,
        url: url.toString(),
        state,
        redirectUri,
        scopes,
        isReady: true,
        missingConfig,
        tokenExchange: config.tokenExchange
    };
}

export function buildGoogleCalendarOAuthStartUrl(request: ExternalOAuthStartRequest): ExternalOAuthStartResult {
    return buildExternalOAuthStartUrl({ ...request, provider: 'google' });
}

export function externalProviderHasWriteScope(provider: ExternalOAuthProviderId, scope: string | null | undefined): boolean {
    if (!scope) return false;
    const config = getExternalOAuthProviderConfig(provider);
    if (!config || config.writeScopes.length === 0) return false;
    const grantedScopes = scope.split(/\s+/).filter(Boolean);
    return config.writeScopes.some((writeScope) => grantedScopes.includes(writeScope));
}

export function mapExternalCalendarToOwnitCalendar(externalCalendar: ExternalCalendar, user: User, provider: ExternalCalendarProvider, syncedAt = new Date().toISOString()): OwnitCalendar {
    return {
        id: ownitExternalCalendarId(user.id, provider.id, externalCalendar.externalId),
        name: externalCalendar.name,
        type: 'personal',
        ownerId: user.id,
        groupId: null,
        color: externalCalendar.color,
        description: externalCalendar.description,
        visibility: 'private',
        isDefault: false,
        isVisible: externalCalendar.isVisible,
        order: externalCalendar.order,
        archivedAt: null,
        deletedAt: null,
        source: provider.id,
        externalId: externalCalendar.externalId,
        provider: provider.id,
        readOnly: true,
        lastSyncedAt: externalCalendar.lastSyncedAt || syncedAt,
        externalUpdatedAt: externalCalendar.lastSyncedAt || syncedAt,
        localUpdatedAt: externalCalendar.lastSyncedAt || syncedAt,
        syncDirection: 'import_only',
        syncDeletionPolicy: 'mark_deleted_only',
        syncStatus: 'synced',
        syncError: null,
        externalWritable: false,
        externalWritePermission: 'not_requested',
        externalWriteScope: '',
        createdAt: externalCalendar.lastSyncedAt || syncedAt,
        updatedAt: externalCalendar.lastSyncedAt || syncedAt
    };
}

export function mapExternalEventToOwnitEvent(externalEvent: ExternalCalendarEvent, calendar: OwnitCalendar, user: User, provider: ExternalCalendarProvider, syncedAt = new Date().toISOString()): OwnitEvent {
    const startDate = normalizeDateKey(externalEvent.startDate, syncedAt.slice(0, 10));
    const endDate = normalizeDateKey(externalEvent.endDate, startDate);
    const createdAt = externalEvent.updatedAt || externalEvent.lastSyncedAt || syncedAt;
    const externalUpdatedAt = externalEvent.updatedAt || externalEvent.lastSyncedAt || syncedAt;
    return {
        id: ownitExternalEventId(user.id, provider.id, externalEvent.externalId),
        title: (externalEvent.title || '(제목 없음)').trim(),
        description: externalEvent.description || '',
        startDate,
        endDate: endDate < startDate ? startDate : endDate,
        startTime: externalEvent.allDay ? null : externalEvent.startTime,
        endTime: externalEvent.allDay ? null : externalEvent.endTime,
        allDay: externalEvent.allDay,
        color: externalEvent.color || calendar.color || OWNIT_NEUTRAL_COLORS.stone,
        category: externalSourceLabel(provider.id),
        calendarId: calendar.id,
        authorId: user.id,
        assigneeId: user.id,
        visibility: 'private',
        repeatRule: externalEvent.repeatRule || null,
        reminders: [],
        completed: false,
        ownerId: user.id,
        groupId: null,
        deletedAt: externalEvent.deleted ? externalUpdatedAt : null,
        syncVersion: 1,
        source: provider.id,
        externalId: externalEvent.externalId,
        provider: provider.id,
        externalCalendarId: externalEvent.calendarExternalId,
        externalCalendarName: externalEvent.calendarName || calendar.name,
        externalRecurringEventId: externalEvent.recurringEventId || null,
        externalTimezone: externalEvent.timezone || 'local',
        readOnly: true,
        lastSyncedAt: externalEvent.lastSyncedAt || syncedAt,
        externalUpdatedAt,
        localUpdatedAt: createdAt,
        syncDirection: 'import_only',
        syncStatus: externalEvent.deleted ? 'deleted_external' : 'synced',
        syncError: externalEvent.deleted ? '외부에서 삭제된 일정입니다. 삭제 전파는 사용자 확인 전까지 보류됩니다.' : null,
        createdAt,
        updatedAt: externalEvent.updatedAt || createdAt
    };
}

export function mapExternalEventToOwnitMapping(externalEvent: ExternalCalendarEvent, ownitEvent: OwnitEvent, calendar: OwnitCalendar, user: User, provider: ExternalCalendarProvider, syncedAt = new Date().toISOString()): ExternalEventMapping {
    const externalUpdatedAt = externalEvent.updatedAt || externalEvent.lastSyncedAt || syncedAt;
    const localUpdatedAt = ownitEvent.updatedAt || ownitEvent.createdAt || syncedAt;
    return {
        id: ownitExternalMappingId(user.id, provider.id, externalEvent.calendarExternalId, externalEvent.externalId),
        eventId: ownitEvent.id,
        calendarId: calendar.id,
        source: provider.id,
        provider: provider.id,
        calendarName: externalEvent.calendarName || calendar.name,
        externalEventId: externalEvent.externalId,
        externalCalendarId: externalEvent.calendarExternalId,
        externalRecurringEventId: externalEvent.recurringEventId || null,
        syncDirection: 'import_only',
        lastSyncedAt: externalEvent.lastSyncedAt || syncedAt,
        externalUpdatedAt,
        localUpdatedAt,
        syncStatus: externalEvent.deleted ? 'deleted_external' : 'synced',
        syncError: null,
        allDay: externalEvent.allDay,
        timezone: externalEvent.timezone || 'local',
        repeatRule: externalEvent.repeatRule || null,
        localDeletedAt: ownitEvent.deletedAt || null,
        externalDeletedAt: externalEvent.deleted ? externalUpdatedAt : null,
        ownerId: user.id,
        groupId: calendar.groupId || null,
        deletedAt: null,
        syncVersion: 1,
        createdAt: ownitEvent.createdAt || syncedAt,
        updatedAt: syncedAt
    };
}

export function normalizeExternalSyncDirection(value: string | null | undefined): CalendarSyncDirection {
    if (value === 'export_only' || value === 'bidirectional') return value;
    return 'import_only';
}

export function normalizeExternalDeletionPolicy(value: string | null | undefined): ExternalCalendarDeletionPolicy {
    if (value === 'mirror_after_confirm') return value;
    return 'mark_deleted_only';
}

export function normalizeExternalWritePermission(value: string | null | undefined): ExternalCalendarWritePermissionStatus {
    if (value === 'requested' || value === 'granted' || value === 'denied') return value;
    return 'not_requested';
}

export function normalizeExternalSyncStatus(value: string | null | undefined): CalendarSyncStatus {
    if (value === 'synced' || value === 'pending_import' || value === 'pending_export' || value === 'write_scope_required' || value === 'conflict' || value === 'error' || value === 'deleted_external' || value === 'deleted_local') return value;
    return 'idle';
}

export function externalSourceLabel(source: ExternalCalendarSource | string | null | undefined): string {
    if (source === 'google') return 'Google';
    if (source === 'microsoft') return 'Microsoft';
    if (source === 'apple') return 'Apple';
    if (source === 'naver') return 'Naver';
    if (source === 'ics_url') return 'ICS';
    return '외부';
}

function createMockExternalCalendar(config: MockProviderConfig, context: ExternalCalendarProviderContext): ExternalCalendar {
    return {
        externalId: `${config.id}-primary`,
        source: config.id,
        provider: config.id,
        name: config.calendarName,
        description: config.description,
        color: config.color,
        isVisible: true,
        readOnly: true,
        lastSyncedAt: context.syncedAt,
        order: config.order
    };
}

function ownitExternalCalendarId(userId: EntityId, source: ExternalCalendarSource, externalId: string): string {
    return `external-cal-${sanitizeIdSegment(userId)}-${source}-${sanitizeIdSegment(externalId)}`;
}

function ownitExternalEventId(userId: EntityId, source: ExternalCalendarSource, externalId: string): string {
    return `external-event-${sanitizeIdSegment(userId)}-${source}-${sanitizeIdSegment(externalId)}`;
}

function ownitExternalMappingId(userId: EntityId, source: ExternalCalendarSource, externalCalendarId: string, externalEventId: string): string {
    return `external-map-${sanitizeIdSegment(userId)}-${source}-${sanitizeIdSegment(externalCalendarId)}-${sanitizeIdSegment(externalEventId)}`;
}

function normalizeDateKey(value: string, fallback: string): ISODateString {
    return /^\d{4}-\d{2}-\d{2}$/.test(value || '') ? value : fallback;
}

function sanitizeIdSegment(value: string): string {
    return (value || 'unknown').trim().toLowerCase().replace(/[^a-z0-9_-]+/g, '-').replace(/^-+|-+$/g, '') || 'unknown';
}
